import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/baseUi/tabs"
import { cn } from "@/modules/lib/utils"
import { BoxStaffTabDayToDay } from "@modules/boxStaff/components/boxStaffDayToDay"
import { BoxStaffTabProfile } from "@modules/boxStaff/components/boxStaffTabProfile"
import { displayIcon, displayRole } from "@modules/boxStaff/entities/boxStaffUtils"
import { useBoxStaff, useBoxStaffActions } from "@modules/boxStaff/hooks/useBoxStaff"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HuAvatarCardSidebar } from "@modules/ui/components/huAvatarCardSidebar"
import { startTransition } from "react"
import { Text } from "@/components/ui/baseUi/text"

type BoxStaffDetailsInfoProps = {
  staffId: string
  className?: string
} & React.ComponentProps<"div">

export function BoxStaffDetailsInfo({ staffId, className, ...props }: BoxStaffDetailsInfoProps) {
  const { data: staffDetailed } = useBoxStaff(staffId)
  const { updateBoxStaff } = useBoxStaffActions(staffId)
  const { getCurrentStackState, setCurrentStackState } = useSidebar()

  const isArchivedStaff = staffDetailed.archived
  const activeIndex = getCurrentStackState("activeTabIndex") ?? (isArchivedStaff ? "1" : "0")

  const selectTab = (index: string) => {
    startTransition(() => {
      setCurrentStackState("activeTabIndex", index)
    })
  }

  return (
    <div className={cn("w-full", className)} {...props}>
      {/* HEADER */}
      <HuAvatarCardSidebar
        id={staffDetailed.userId}
        avatar={staffDetailed.publicProfile?.avatar ?? undefined}
        firstname={staffDetailed.firstname}
        lastname={staffDetailed.lastname}
        username={staffDetailed.username}
        prefixUsername={staffDetailed.username ? "@" : undefined}
        nationality={staffDetailed.publicProfile?.nationality ?? undefined}
        email={staffDetailed.email}
        logo={displayIcon("text-textDefautl-body", 50, staffDetailed.role)}
      />
      {/* NAVIGATION TABS */}
      <div className="mt-4 flex w-full">
        <Tabs defaultValue={activeIndex} className="flex w-full flex-col">
          <TabsList variant="default">
            {!isArchivedStaff && (
              <TabsTrigger variant="default" onClick={() => selectTab("0")} value="0">
                {word("box.member.detailsInfo.dayToDay")}
              </TabsTrigger>
            )}
            <TabsTrigger variant="default" onClick={() => selectTab("1")} value="1">
              {word("box.member.detailsInfo.profile")}
            </TabsTrigger>
            {isArchivedStaff && (
              <TabsTrigger variant="default" onClick={() => selectTab("2")} value="2">
                {word("box.staff.staffHistory")}
              </TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="0" className="flex w-full px-5">
            <BoxStaffTabDayToDay staffDetailed={staffDetailed} isLoading={false} updateBoxStaff={updateBoxStaff} />
          </TabsContent>
          <TabsContent value="1" className="flex w-full px-5">
            <BoxStaffTabProfile staffDetailed={staffDetailed} />
          </TabsContent>
          <TabsContent value="2" className="mt-2 flex flex-col gap-12 px-5">
            <div className="flex flex-row items-end gap-4">
              <Text fontStyle="TM" className="text-textDefault-body">
                {word("box.archived.table.staff.lastPosition")} :
              </Text>
              <Text fontStyle="BS" className="text-textDefault-body">
                {displayRole(staffDetailed.role)}
              </Text>
            </div>
            <Text fontStyle="BS" className="text-textDefault-body italic">
              {word("box.staff.staffHistoryComingSoon")}
            </Text>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
